// Metro regions used to group events by proximity to the user's chosen city.
//
// A region is a *scene catchment area*, not a strict metro boundary: the
// Mendocino and Sierra festival sites map to the Bay Area and Sacramento
// because that is who drives to them, even though they are hours out.
//
// The region list is aligned to the 16 regional feeds 19hz.info publishes,
// since that is where imported events come from — see plans/home-region.md.
// Feeds and regions are deliberately not 1:1: an event's region comes from its
// venue city, so one feed fans out to several regions on its own.

export interface Region {
  id: string
  name: string
  country: 'US' | 'CA' | 'CL'
  /**
   * State or province the centroid sits in. A spillover city in a neighbouring
   * state doesn't change it: Vancouver WA is in Portland's catchment, but
   * Portland is still the Oregon scene. Used to tell whether a region is the
   * only one we cover in a state.
   */
  state: string
  /** [latitude, longitude] — distance between centroids drives the tiers. */
  centroid: [number, number]
  /**
   * Normalized city keys that map here: either `'oakland'` or, when a city
   * name is claimed by more than one region, `'albany, ca'`. Lookup tries the
   * qualified key first, then the bare one.
   */
  cities: string[]
  /** IANA zones that resolve to this region. */
  timezones: string[]
}

/**
 * The unfiltered view. Used when the device timezone matches no region, and
 * offered as a deliberate choice in the picker, so there is never an
 * "unconfigured" state to nag the user about.
 */
export const EVERYWHERE_REGION_ID = 'everywhere'

export const REGIONS: Region[] = [
  {
    id: 'bay-area',
    name: 'Bay Area',
    country: 'US',
    state: 'CA',
    centroid: [37.77, -122.42],
    timezones: ['America/Los_Angeles'],
    cities: [
      'san francisco', 'oakland', 'berkeley', 'san jose', 'santa clara',
      'sunnyvale', 'cupertino', 'campbell', 'milpitas', 'alameda', 'san leandro',
      'richmond, ca', 'albany, ca', 'orinda', 'sausalito', 'daly city',
      'santa cruz', 'santa rosa', 'sebastopol', 'napa', 'american canyon',
      'walnut creek', 'fremont', 'los gatos',
      // North Coast / Mendocino festival sites — Bay Area catchment
      'hopland', 'piercy', 'navarro', 'redwood valley', 'cazadero', 'arcata',
      'laytonville',
      // CA-qualified: Humboldt also names places in other states and Chicago's Humboldt Park (#596)
      'humboldt, ca',
      // Not cities: 19hz uses these when a party's venue is secret until the
      // day of. On the Bay Area listing they always mean this scene, and the
      // alternative is filing the event under "Elsewhere" for everyone.
      'bay area', 'norcal',
      // auto-mapped: city in Solano County, California, ~40 km from the centroid
      'vallejo',
      // auto-mapped: city in Santa Clara County, California, ~52 km from the centroid
      'mountain view',
      // auto-mapped: town in Marin County, California, ~19 km from the centroid
      'mill valley',
      // auto-mapped: suburb in Oakland, Alameda County, ~12 km from the centroid
      'west oakland',
      // auto-mapped: city in Alameda County, California, ~58 km from the centroid
      'livermore',
      // auto-mapped: village in Santa Cruz County, California, ~86 km from the centroid
      'felton',
      // auto-mapped: highway in Auzerais Avenue, San Jose, ~68 km from the centroid
      'downtown san jose',
      // auto-mapped: city in Sonoma County, California, ~55 km from the centroid
      'petaluma',
      // auto-mapped: town in Alameda County, California, ~49 km from the centroid
      'pleasanton',
      // Monterey Peninsula — ~120 km from SF; its scene draws from the Bay Area
      'monterey',
    ],
  },
  {
    id: 'sacramento',
    name: 'Sacramento',
    country: 'US',
    state: 'CA',
    centroid: [38.58, -121.49],
    timezones: ['America/Los_Angeles'],
    cities: [
      'sacramento', 'west sacramento', 'roseville', 'folsom', 'davis',
      'stockton', 'chico', 'redding', 'yuba city', 'marysville, ca',
      // Sierra foothills / Delta festival sites — Sacramento catchment
      'wilseyville', 'somerset', 'isleton', 'tuolumne', 'shasta', 'pioneer, ca',
      'nevada city',
      // auto-mapped: village in Stanislaus County, California, ~122 km from the centroid
      'waterford',
      // auto-mapped: town in Nevada County, California, ~80 km from the centroid
      'grass valley',
      // auto-mapped: town in Placer County, California, ~39 km from the centroid
      'lincoln, ca',
    ],
  },
  {
    id: 'los-angeles',
    name: 'Los Angeles',
    country: 'US',
    state: 'CA',
    centroid: [34.05, -118.24],
    timezones: ['America/Los_Angeles'],
    cities: [
      'los angeles', 'long beach', 'pasadena', 'santa monica', 'anaheim',
      'santa ana', 'irvine', 'riverside', 'san bernardino', 'pomona',
      'indio', 'ventura', 'santa barbara', 'bradley',
    ],
  },
  {
    id: 'central-valley',
    name: 'Central Valley',
    country: 'US',
    state: 'CA',
    centroid: [36.74, -119.79],
    timezones: ['America/Los_Angeles'],
    // No 19hz feed, so nothing imports here automatically — but venues do turn
    // up (Fulton 55 in Fresno), and without a region of their own they are
    // 300 km from the nearest one and would sit permanently in "Elsewhere".
    cities: [
      'fresno', 'clovis', 'madera', 'visalia', 'merced', 'modesto', 'bakersfield',
      // auto-mapped: town in Merced County, California, ~99 km from the centroid
      'atwater',
    ],
  },
  {
    id: 'san-diego',
    name: 'San Diego',
    country: 'US',
    state: 'CA',
    centroid: [32.72, -117.16],
    timezones: ['America/Los_Angeles'],
    cities: ['san diego', 'chula vista', 'oceanside', 'encinitas', 'del mar'],
  },
  {
    id: 'las-vegas',
    name: 'Las Vegas',
    country: 'US',
    state: 'NV',
    centroid: [36.17, -115.14],
    timezones: ['America/Los_Angeles'],
    cities: ['las vegas', 'henderson', 'paradise, nv'],
  },
  {
    id: 'seattle',
    name: 'Seattle',
    country: 'US',
    state: 'WA',
    centroid: [47.61, -122.33],
    timezones: ['America/Los_Angeles'],
    // 'des moines, wa' is qualified: the bare name resolves to Des Moines, Iowa.
    cities: [
      'seattle', 'tacoma', 'bellevue', 'everett', 'olympia', 'george, wa',
      'des moines, wa',
      // Cascades campout site ~2h east over US-97 — Seattle's catchment.
      'blewett pass',
      // auto-mapped: town in King County, Washington, ~34 km from the centroid
      'auburn',
      // auto-mapped: town in Snohomish County, Washington, ~66 km from the centroid
      'arlington',
      // auto-mapped: town in King County, Washington, ~17 km from the centroid.
      // Qualified: Redmond OR is Portland's.
      'redmond, wa',
      // auto-mapped: city in Island County, Washington, ~48 km from the centroid
      'langley',
      // auto-mapped: town in Snohomish County, Washington, ~24 km from the centroid
      'lynnwood',
    ],
  },
  {
    id: 'portland',
    name: 'Portland',
    country: 'US',
    state: 'OR',
    centroid: [45.52, -122.68],
    timezones: ['America/Los_Angeles'],
    // 'cave junction' is Southern Oregon, ~4.5h out, but Portland is the
    // nearest scene we cover and where RA files its events.
    cities: ['portland, or', 'eugene', 'salem, or', 'bend', 'vancouver, wa',
      'medford', 'grants pass', 'cave junction',
      // Central Oregon and the south coast. Both are hours out, but Portland is
      // the only Oregon scene we cover. 'redmond' is qualified because Redmond
      // WA, a Seattle suburb, claims the bare name.
      'redmond, or', 'coos bay',
      // Linn County, Rave River's campout site. Flagged in #379 and never
      // landed.
      'scio, or',
      // auto-mapped: town in Multnomah County, Oregon, ~23 km from the centroid
      'troutdale',
      // auto-mapped: town in Wasco County, Oregon, ~117 km from the centroid
      'the dalles',
    ],
  },
  {
    id: 'vancouver-bc',
    name: 'Vancouver',
    country: 'CA',
    state: 'BC',
    centroid: [49.28, -123.12],
    timezones: ['America/Vancouver'],
    cities: [
      'vancouver, bc', 'north vancouver', 'burnaby', 'surrey', 'victoria',
      'kelowna', 'nelson', 'salmo', 'squamish', 'whistler', 'nanaimo',
      'cumberland',
      // auto-mapped: island in Salt Spring Island Electoral Area, Capital Regional District, ~58 km from the centroid
      'salt spring island',
      // auto-mapped: political in Vancouver, Metro Vancouver Regional District, ~4 km from the centroid
      'east vancouver',
      // auto-mapped: city in Fraser Valley Regional District, British Columbia, ~63 km from the centroid
      'abbotsford',
      // auto-mapped: city in Cariboo Regional District, British Columbia, ~324 km from the centroid.
      // Past the nearby band, but the only scene we cover in BC.
      'williams lake, bc',
    ],
  },
  {
    id: 'calgary',
    name: 'Calgary',
    country: 'CA',
    state: 'AB',
    centroid: [51.05, -114.07],
    timezones: ['America/Edmonton'],
    cities: ['calgary', 'edmonton', 'banff', 'canmore', 'bragg creek'],
  },
  {
    id: 'toronto',
    name: 'Toronto',
    country: 'CA',
    state: 'ON',
    centroid: [43.65, -79.38],
    timezones: ['America/Toronto'],
    cities: ['toronto', 'mississauga', 'hamilton, on', 'ottawa', 'montreal',
      // Toronto districts and Durham Region — the resolver had these but rolled
      // them back on a bad neighbour (#383).
      'east york', 'york, on', 'pickering',
      // Parry Sound District, ~2.5h north. Past the nearby band, but the only
      // Ontario scene we cover.
      'burks falls, on',
      // auto-mapped: city in York Region, Ontario, ~20 km from the centroid
      'vaughan',
      // auto-mapped: city in Region of Waterloo, Southwestern Ontario, ~94 km from the centroid
      'waterloo',
      // auto-mapped: hamlet in Whitchurch-Stouffville, York Region, ~32 km from the centroid
      'gormley',
    ],
  },
  {
    id: 'chicago',
    name: 'Chicago',
    country: 'US',
    state: 'IL',
    centroid: [41.88, -87.63],
    timezones: ['America/Chicago'],
    cities: [
      'chicago', 'bridgeview', 'lisle', 'evanston', 'rosemont', 'oak park',
      'naperville', 'aurora, il', 'joliet', 'cicero', 'milwaukee',
      'merrionette park',
      // auto-mapped: village in Stickney Township, Cook County, ~17 km from the centroid
      'bedford park',
      // auto-mapped: city in Lake County, Illinois, ~56 km from the centroid
      'waukegan',
      // auto-mapped: village in Proviso Township, Cook County, ~21 km from the centroid
      'stone park',
      // auto-mapped: town in Palatine Township, Cook County, ~43 km from the centroid
      'palatine',
      // auto-mapped: village in Henry County, Illinois, ~223 km from the centroid.
      // Past the nearby band, but the only scene we cover in IL.
      'cambridge, il',
      // auto-mapped: suburb in Chicago, South Chicago Township, ~5 km from the centroid.
      // State-qualified so a Bridgeport, CT venue doesn't land in chicago.
      'bridgeport, il',
      // auto-mapped: county in Illinois, United States, ~85 km from the centroid
      'mchenry',
      // auto-mapped: village in Orland Township, Cook County, ~37 km from the centroid
      'orland hills',
      // auto-mapped: town in Schaumburg Township, Cook County, ~41 km from the centroid
      'schaumburg',
      // auto-mapped: town in Geneva Township, Kane County, ~56 km from the centroid
      'geneva',
      // auto-mapped: town in Lake County, Illinois, ~52 km from the centroid
      'libertyville, il',
    ],
  },
  {
    id: 'minneapolis',
    name: 'Minneapolis',
    country: 'US',
    state: 'MN',
    centroid: [44.98, -93.27],
    timezones: ['America/Chicago'],
    // Twin Cities as one scene. 'roseville, mn' is qualified — the bare name
    // belongs to Sacramento; 'rochester, mn' and 'bloomington, mn' because the
    // bare names are better-known cities elsewhere.
    cities: [
      'minneapolis', 'st paul', 'saint paul', 'st louis park', 'edina',
      'richfield', 'bloomington, mn', 'eden prairie', 'hopkins', 'golden valley',
      'brooklyn park', 'maple grove', 'roseville, mn', 'burnsville', 'eagan',
      'st cloud', 'rochester, mn', 'duluth',
    ],
  },
  {
    id: 'detroit',
    name: 'Detroit',
    country: 'US',
    state: 'MI',
    centroid: [42.33, -83.05],
    timezones: ['America/Detroit'],
    cities: ['detroit', 'ann arbor', 'ferndale', 'pontiac'],
  },
  {
    id: 'ohio',
    name: 'Ohio',
    country: 'US',
    state: 'OH',
    // Columbus, central to all three metros. Distance from here is what makes
    // Legend Valley "drivable" for Detroit, Chicago, Toronto and DC — an
    // unmapped city has no position at all, so its festivals are far from
    // everyone.
    centroid: [39.96, -83.0],
    timezones: ['America/New_York'],
    // One region for the state rather than three metros, on the `iowa-nebraska`
    // precedent: low volume for us, and Cleveland/Cincinnati would otherwise
    // each need their own. 'newark, oh' is qualified — the bare name is NJ.
    cities: [
      'columbus', 'thornville', 'newark, oh', 'cleveland', 'cincinnati',
      'dayton', 'akron', 'toledo',
    ],
  },
  {
    id: 'boston',
    name: 'Boston',
    country: 'US',
    state: 'MA',
    centroid: [42.36, -71.06],
    timezones: ['America/New_York'],
    cities: ['boston', 'cambridge, ma', 'somerville', 'worcester', 'providence'],
  },
  {
    id: 'new-york',
    name: 'New York',
    country: 'US',
    state: 'NY',
    centroid: [40.71, -74.01],
    timezones: ['America/New_York'],
    // RA writes the neighbourhood, not the borough, as a venue's city, so the
    // outer-borough names below are cities as far as the address is concerned.
    // Qualified where the bare name is claimed elsewhere (Ridgewood NJ,
    // Corona CA, Astoria OR).
    cities: [
      'new york', 'new york city', 'brooklyn', 'queens', 'bronx', 'staten island',
      'newark', 'jersey city', 'hoboken', 'yonkers',
      'ridgewood, ny', 'maspeth', 'corona, ny', 'astoria, ny', 'long island city',
      // Catskills / Hudson Valley party sites — New York catchment, same way
      // the Mendocino sites belong to the Bay Area.
      'monticello, ny', 'oak hill, ny', 'catskill',
      // Poconos campout site — 'lake como, pa' is qualified, the bare name is NJ.
      'lake como, pa',
      // auto-mapped: suburb in New York County, New York, ~6 km from the centroid
      'manhattan',
      // auto-mapped: census in Town of Southampton, Suffolk County, ~126 km from the centroid
      'hampton bays',
      // Hamptons, next to Hampton Bays (#473). Qualified: there's a Westhampton, MA too.
      'westhampton, ny',
      // auto-mapped: town in Town of Brookhaven, Suffolk County, ~84 km from the centroid
      'patchogue',
    ],
  },
  {
    id: 'philadelphia',
    name: 'Philadelphia',
    country: 'US',
    state: 'PA',
    centroid: [39.95, -75.17],
    timezones: ['America/New_York'],
    // Greater Philly on both banks of the Delaware — the NJ/DE side is a
    // Philadelphia scene, not a New York or DC one. 'camden, nj' and
    // 'wilmington, de' are qualified because the bare names are claimed by
    // better-known cities elsewhere. Pittsburgh is its own region below: RA
    // area 80 is all of Pennsylvania, and that is a different scene 450 km away.
    cities: [
      'philadelphia', 'philly', 'camden, nj', 'cherry hill', 'collingswood',
      'conshohocken', 'ardmore, pa', 'upper darby', 'norristown', 'king of prussia',
      'wilmington, de', 'atlantic city', 'allentown, pa',
      // auto-mapped: city in Northampton County, Pennsylvania, ~83 km from the centroid
      'easton',
      // auto-mapped: village in Caernarvon Township, Berks County, ~65 km from the centroid
      'morgantown',
      // Lancaster County, ~100 km west of the centroid — Philly is the nearest scene
      // we cover (#582). Qualified: Lancaster CA (LA County) and others share the name.
      'lancaster, pa',
    ],
  },
  {
    // Split out of RA area 80 (all of Pennsylvania) once one Philadelphia run
    // held back 13 Pittsburgh-area events (#416). Not a discovery metro yet —
    // its events arrive through the Philadelphia RA feed.
    id: 'pittsburgh',
    name: 'Pittsburgh',
    country: 'US',
    state: 'PA',
    centroid: [40.44, -79.99],
    timezones: ['America/New_York'],
    cities: ['pittsburgh', 'braddock, pa', 'homestead, pa', 'millvale', 'lawrenceville, pa'],
  },
  {
    id: 'dc',
    name: 'Washington DC',
    country: 'US',
    state: 'DC',
    centroid: [38.91, -77.04],
    timezones: ['America/New_York'],
    // 'washington dc': "Washington D.C." with the periods normalized away (#609)
    cities: ['washington', 'washington, dc', 'washington dc', 'baltimore', 'arlington, va',
      // auto-mapped: city in Virginia, 20030, ~24 km from the centroid
      'fairfax',
      // auto-mapped: statistical in Montgomery County, Maryland, ~10 km from the centroid
      'silver spring',
      // auto-mapped: city in Virginia, 22046, ~12 km from the centroid
      'falls church',
      // auto-mapped: statistical in Baltimore County, Maryland, ~47 km from the centroid
      'catonsville',
      // auto-mapped: hamlet in Germantown, Montgomery County, ~38 km from the centroid
      'boyds',
      // auto-mapped: census in Fairfax County, Virginia, ~16 km from the centroid
      'annandale',
      // auto-mapped: statistical in Howard County, Maryland, ~35 km from the centroid
      'columbia',
      // auto-mapped: statistical in White Plains, Charles County, ~36 km from the centroid
      'waldorf',
    ],
  },
  {
    id: 'richmond',
    name: 'Richmond',
    country: 'US',
    state: 'VA',
    centroid: [37.54, -77.44],
    timezones: ['America/New_York'],
    // ~160 km from DC — too far for a club night, so its own scene. Northern
    // Virginia (Arlington, Alexandria) stays with DC. Arrington is Lockn's
    // farm site, which is nearer Richmond and Charlottesville than DC.
    cities: ['richmond, va', 'charlottesville', 'norfolk', 'virginia beach', 'arrington, va',
      // auto-mapped: county in Virginia, United States, ~270 km from the centroid.
      // Past the nearby band, but the only scene we cover in VA.
      'floyd, va',
    ],
  },
  {
    id: 'atlanta',
    name: 'Atlanta',
    country: 'US',
    state: 'GA',
    centroid: [33.75, -84.39],
    timezones: ['America/New_York'],
    cities: ['atlanta', 'athens, ga', 'savannah', 'nashville'],
  },
  {
    id: 'miami',
    name: 'Miami',
    country: 'US',
    state: 'FL',
    centroid: [25.76, -80.19],
    timezones: ['America/New_York'],
    cities: ['miami', 'miami beach', 'fort lauderdale', 'orlando', 'tampa'],
  },
  {
    id: 'austin',
    name: 'Austin',
    country: 'US',
    state: 'TX',
    centroid: [30.27, -97.74],
    timezones: ['America/Chicago'],
    cities: ['austin', 'san antonio'],
  },
  {
    id: 'dallas',
    name: 'Dallas',
    country: 'US',
    state: 'TX',
    centroid: [32.78, -96.8],
    timezones: ['America/Chicago'],
    cities: ['dallas', 'fort worth', 'arlington, tx', 'denton'],
  },
  {
    id: 'houston',
    name: 'Houston',
    country: 'US',
    state: 'TX',
    centroid: [29.76, -95.37],
    timezones: ['America/Chicago'],
    cities: ['houston', 'galveston'],
  },
  {
    // Far west Texas, ~900 km from any other Texas region and on Mountain time
    // rather than Central — its own region rather than an Austin/Dallas outlier.
    id: 'el-paso',
    name: 'El Paso',
    country: 'US',
    state: 'TX',
    centroid: [31.76, -106.49],
    timezones: ['America/Denver'],
    cities: ['el paso', 'las cruces'],
  },
  {
    id: 'phoenix',
    name: 'Phoenix',
    country: 'US',
    state: 'AZ',
    centroid: [33.45, -112.07],
    timezones: ['America/Phoenix'],
    cities: ['phoenix', 'scottsdale', 'tempe', 'mesa', 'tucson', 'avondale'],
  },
  {
    id: 'denver',
    name: 'Denver',
    country: 'US',
    state: 'CO',
    centroid: [39.74, -104.99],
    timezones: ['America/Denver'],
    cities: ['denver', 'boulder', 'colorado springs', 'fort collins', 'morrison'],
  },
  {
    id: 'iowa-nebraska',
    name: 'Iowa & Nebraska',
    country: 'US',
    state: 'NE',
    centroid: [41.26, -95.93],
    timezones: ['America/Chicago'],
    // Deliberate exception to the metro rule: the 19hz feed is bi-state and
    // low-volume, so splitting into Omaha / Des Moines is not worth it.
    cities: ['omaha', 'lincoln, ne', 'des moines', 'iowa city', 'cedar rapids', 'libertyville, ia'],
  },
  {
    id: 'kansas-city',
    name: 'Kansas City',
    country: 'US',
    state: 'MO',
    centroid: [39.1, -94.58],
    timezones: ['America/Chicago'],
    // Bi-state metro; La Cygne is Dancefestopia's site an hour south. Ozark AR
    // (Mulberry Mountain, Wakaan) has no Arkansas scene; KC is its nearest.
    cities: ['kansas city', 'overland park', 'independence', 'lawrence', 'la cygne', 'ozark, ar'],
  },
  {
    id: 'santiago',
    name: 'Santiago',
    country: 'CL',
    // Región Metropolitana. Chile has no two-letter postal codes; RM is the
    // conventional abbreviation and what STATE_CODES maps the long form to.
    state: 'RM',
    centroid: [-33.45, -70.66],
    timezones: ['America/Santiago'],
    // RA addresses name the comuna, not the city, so the region claims every
    // comuna of Gran Santiago.
    cities: [
      'santiago', 'santiago centro', 'providencia', 'las condes', 'vitacura',
      'lo barnechea', 'ñuñoa', 'la reina', 'macul', 'peñalolén', 'la florida',
      'recoleta', 'independencia', 'conchalí', 'huechuraba', 'quilicura',
      'renca', 'quinta normal', 'estación central', 'cerrillos', 'maipú',
      'pudahuel', 'lo prado', 'cerro navia', 'san miguel', 'san joaquín',
      'pedro aguirre cerda', 'lo espejo', 'la cisterna', 'el bosque',
      'la granja', 'san ramón', 'la pintana', 'puente alto', 'san bernardo',
      'colina', 'lampa', 'chicureo', 'pirque', 'buin', 'talagante',
      'peñaflor', 'calera de tango', 'san josé de maipo', 'curacaví',
    ],
  },
]

const REGION_BY_ID = new Map(REGIONS.map(r => [r.id, r]))

export function getRegion(id: string | undefined): Region | undefined {
  return id ? REGION_BY_ID.get(id) : undefined
}

/** Lowercase, strip accents and punctuation so `San José` matches `San Jose`. */
export function normalizePlaceName(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[.']/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

// Bare city → region, and `city, state` → region for names more than one
// region claims (Portland OR vs ME, Vancouver BC vs WA, Albany CA vs NY).
const CITY_INDEX = new Map<string, string>()
for (const region of REGIONS) {
  for (const city of region.cities) CITY_INDEX.set(normalizePlaceName(city), region.id)
}

/** Two-letter state / province / region code, or undefined if it isn't one. */
function stateCode(state: string | undefined): string | undefined {
  if (!state) return undefined
  const s = normalizePlaceName(state)
  // Seeded data holds junk in this field on a few records ('USA'), so only
  // two-letter codes count.
  return s.length === 2 ? s : undefined
}

/**
 * Region for a venue. Matches on city, using state only to disambiguate city
 * names more than one region claims. Country is deliberately not a parameter:
 * no city name a region claims collides across a border, and seeded venue
 * records hold inconsistent values in that field anyway.
 */
export function regionForVenue(city: string | undefined, state?: string): string | undefined {
  if (!city) return undefined
  const c = normalizePlaceName(city)
  const s = stateCode(state)
  if (s) {
    const qualified = CITY_INDEX.get(`${c}, ${s}`)
    if (qualified) return qualified
  }
  // A scene name in the city field ('South Bay') is a venue the promoter
  // placed by area. Without the alias it reads as unmapped, and the city
  // resolver geocodes it to whichever same-named place Nominatim prefers —
  // LA's South Bay, from a Bay Area listing (#427).
  return CITY_INDEX.get(c) ?? SCENE_ALIASES.get(c)
}

/**
 * Region for an event that has no venue record, only a free-text location like
 * `San Francisco, CA` or `Bay Area, California`. Best-effort: tries the whole
 * string, then the part before the first comma.
 */
export function regionForFreeText(location: string | undefined): string | undefined {
  if (!location) return undefined
  const whole = normalizePlaceName(location)
  const direct = CITY_INDEX.get(whole) ?? SCENE_ALIASES.get(whole)
  if (direct) return direct

  const [head, tail] = whole.split(',').map(p => p.trim())
  if (!head) return undefined
  const s = stateCodeOf(tail)?.toLowerCase()
  // Promoters write a location as the places it spans — 'Manhattan & Brooklyn',
  // 'San Francisco/Oakland' — so each named place gets its own lookup (#389).
  for (const part of head.split(/\s*(?:\/|&|\band\b)\s*/)) {
    if (!part) continue
    if (s) {
      const qualified = CITY_INDEX.get(`${part}, ${s}`)
      if (qualified) return qualified
    }
    // 'bay area' and similar scene names that aren't cities
    const region = CITY_INDEX.get(part) ?? SCENE_ALIASES.get(part)
    if (region) return region
  }
  return undefined
}

/**
 * Region for an event: the stamped `regionId` when present (#447), else derived
 * from its venue, else from its free-text location. The one resolver every
 * region-aware surface (browse grouping, the search index) goes through.
 */
export function eventRegionId(
  event: { regionId?: string; location?: string },
  venue?: { city: string; state?: string },
): string | undefined {
  return event.regionId ?? (venue ? regionForVenue(venue.city, venue.state) : regionForFreeText(event.location))
}

/**
 * What an event's stamped `regionId` should be: `eventRegionId` with the
 * current stamp ignored, so a stale one never confirms itself. Undefined means
 * the event should carry no stamp.
 *
 * Drafts carry none: discovery queries filter on the stamp, so leaving it off
 * keeps unpublished events out of their reads and out of the picker's counts.
 * Publishing is an event write, which stamps it then.
 */
export function expectedRegionStamp(
  event: { location?: string; published?: boolean },
  venue?: { city: string; state?: string },
): string | undefined {
  if (event.published === false) return undefined
  return eventRegionId({ location: event.location }, venue)
}

/**
 * Regions whose events a discovery query fetches for `homeRegionId`, split by
 * type the same way `isNearby` splits them: parties reach nearby regions,
 * festivals reach drivable ones. Both include the home region itself.
 */
export function discoveryRegionIds(homeRegionId: string): { party: string[]; festival: string[] } {
  const party: string[] = []
  const festival: string[] = []
  for (const r of REGIONS) {
    if (isNearby(homeRegionId, r.id, 'party')) party.push(r.id)
    if (isNearby(homeRegionId, r.id, 'festival')) festival.push(r.id)
  }
  return { party, festival }
}

const SCENE_ALIASES = new Map<string, string>([
  ['bay area', 'bay-area'],
  ['sf bay area', 'bay-area'],
  ['san francisco bay area', 'bay-area'],
  ['east bay', 'bay-area'],
  ['south bay', 'bay-area'],
  ['north bay', 'bay-area'],
  // The Mendocino festival sites are Bay Area catchment (see the region's
  // cities list); a promoter naming the county rather than the site means the same.
  ['mendocino county', 'bay-area'],
  ['northern california', 'bay-area'],
  ['southern california', 'los-angeles'],
  ['socal', 'los-angeles'],
  ['nyc', 'new-york'],
])

// Normalized full name → code, US states and Canadian provinces. Free-text
// locations spell the state out ('East Bay, California') as often as they
// code it, and the geocoder's responses always spell it out.
const STATE_CODES: Record<string, string> = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA',
  colorado: 'CO', connecticut: 'CT', delaware: 'DE', florida: 'FL', georgia: 'GA',
  hawaii: 'HI', idaho: 'ID', illinois: 'IL', indiana: 'IN', iowa: 'IA',
  kansas: 'KS', kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD',
  massachusetts: 'MA', michigan: 'MI', minnesota: 'MN', mississippi: 'MS',
  missouri: 'MO', montana: 'MT', nebraska: 'NE', nevada: 'NV',
  'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
  'north carolina': 'NC', 'north dakota': 'ND', ohio: 'OH', oklahoma: 'OK',
  oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', tennessee: 'TN', texas: 'TX', utah: 'UT', vermont: 'VT',
  virginia: 'VA', washington: 'WA', 'west virginia': 'WV', wisconsin: 'WI',
  wyoming: 'WY', 'district of columbia': 'DC',
  alberta: 'AB', 'british columbia': 'BC', manitoba: 'MB', 'new brunswick': 'NB',
  'newfoundland and labrador': 'NL', 'nova scotia': 'NS', ontario: 'ON',
  'prince edward island': 'PE', quebec: 'QC',
  saskatchewan: 'SK', yukon: 'YT', 'northwest territories': 'NT', nunavut: 'NU',
  'region metropolitana': 'RM', 'region metropolitana de santiago': 'RM',
  'santiago metropolitan region': 'RM',
}

/** Two-letter code for a state/province given by name or code; undefined for anything else. */
export function stateCodeOf(name: string | undefined): string | undefined {
  if (!name) return undefined
  const n = normalizePlaceName(name)
  if (/^[a-z]{2}$/.test(n)) return n.toUpperCase()
  return STATE_CODES[n]
}

/**
 * Which region we assume when a timezone covers several. Declared explicitly
 * rather than read off REGIONS order, so reordering that list can't silently
 * change what a first-run user sees. Ordered by current content volume —
 * revisit as regional coverage grows.
 */
const TIMEZONE_DEFAULT: Record<string, string> = {
  'America/Los_Angeles': 'bay-area',
  'America/Chicago': 'chicago',
  'America/New_York': 'new-york',
  'America/Denver': 'denver',
}

/**
 * Every region a device timezone could mean. Several zones cover more than one
 * — `America/Los_Angeles` alone spans six, including Seattle and Portland —
 * which is why the guess is always visible and one tap from being corrected.
 */
export function regionsForTimezone(timezone: string | undefined): string[] {
  if (!timezone) return []
  return REGIONS.filter(r => r.timezones.includes(timezone)).map(r => r.id)
}

/** Best single guess for a timezone, or `everywhere` when we cover none of it. */
export function regionForTimezone(timezone: string | undefined): string {
  if (!timezone) return EVERYWHERE_REGION_ID
  const candidates = regionsForTimezone(timezone)
  if (candidates.length === 0) return EVERYWHERE_REGION_ID
  const preferred = TIMEZONE_DEFAULT[timezone]
  return preferred && candidates.includes(preferred) ? preferred : candidates[0]
}

const EARTH_RADIUS_KM = 6371
const toRad = (deg: number) => (deg * Math.PI) / 180

/** Great-circle distance between two [latitude, longitude] points. */
export function haversineKm(a: [number, number], b: [number, number]): number {
  const [lat1, lon1] = a
  const [lat2, lon2] = b
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h))
}

export function regionDistanceKm(a: string, b: string): number | undefined {
  const ra = REGION_BY_ID.get(a)
  const rb = REGION_BY_ID.get(b)
  if (!ra || !rb) return undefined
  if (ra.id === rb.id) return 0
  return haversineKm(ra.centroid, rb.centroid)
}

/**
 * Nearest region centroid to a geocoded point, for deciding which catchment a
 * city we don't yet list belongs to. Centroids are city centres, not catchment
 * shapes, so the distance is a rough signal — callers gate on it rather than
 * trusting the nearest region outright.
 */
export function nearestRegionToPoint(
  lat: number,
  lon: number,
): { regionId: string; km: number } | undefined {
  let best: { regionId: string; km: number } | undefined
  for (const region of REGIONS) {
    const km = haversineKm([lat, lon], region.centroid)
    if (!best || km < best.km) best = { regionId: region.id, km }
  }
  return best
}

/**
 * The one region we cover in a state, if there is exactly one. When a state
 * holds a single scene, a city in it has no other catchment to belong to —
 * which is what lets the city resolver map an outlying city sitting past the
 * `nearby` band without guessing between two plausible metros.
 */
export function soleRegionOfState(state: string | undefined): Region | undefined {
  if (!state) return undefined
  const code = state.trim().toUpperCase()
  const matches = REGIONS.filter(r => r.state === code)
  return matches.length === 1 ? matches[0] : undefined
}

export type ProximityTier = 'same' | 'nearby' | 'drivable' | 'far'

export const NEARBY_MAX_KM = 150
// 600 rather than 500 so the LA <-> Bay Area corridor (559 km) counts as
// drivable. That corridor is real for festivals — Lightning in a Bottle draws
// from both — and `drivable` only ever admits festivals, never parties.
export const DRIVABLE_MAX_KM = 600

export function proximityTier(
  homeRegionId: string | undefined,
  eventRegionId: string | undefined,
): ProximityTier {
  if (!homeRegionId || !eventRegionId) return 'far'
  if (homeRegionId === eventRegionId) return 'same'
  const km = regionDistanceKm(homeRegionId, eventRegionId)
  if (km === undefined) return 'far'
  if (km <= NEARBY_MAX_KM) return 'nearby'
  if (km <= DRIVABLE_MAX_KM) return 'drivable'
  return 'far'
}

/**
 * Whether an event counts as "near you". Festivals reach further than parties
 * because people travel to them — nothing is ever hidden either way, this only
 * decides which group an event lands in.
 */
export function isNearby(
  homeRegionId: string | undefined,
  eventRegionId: string | undefined,
  eventType: 'festival' | 'party',
): boolean {
  if (!homeRegionId || homeRegionId === EVERYWHERE_REGION_ID) return false
  const tier = proximityTier(homeRegionId, eventRegionId)
  if (tier === 'same' || tier === 'nearby') return true
  return eventType === 'festival' && tier === 'drivable'
}
